import { get, post } from "@/services/api.services";
import {
    AllExperimentFactorsConfig,
    SavedAdHocFactor,
    SavedApplicationFactor,
    SavedGlobalRegistryFactor,
} from "@/services/conditions/conditionFactors/conditionFactorsAPI";
import { Treatment } from "@/services/treatments/treatmentsAPI";
import { SavedObject } from "@/services/commonModels";
import { equalIgnoringOrder } from "@/lib/arrayUtils";

export type BaseCondition = {
    name: string;
    number: number;
    experimentId: number;
    treatmentId: string;
    thingInstanceDataId: string | null;
};

export type SavedBaseCondition = BaseCondition & SavedObject<string>;

export type Condition = SavedBaseCondition & {
    treatment: Treatment;
    applicationFactors: SavedApplicationFactor[];
    adHocFactors: SavedAdHocFactor[];
    globalRegistryFactors: SavedGlobalRegistryFactor[];
};

export type ManageConditionsDTO = {
    allFactors: AllExperimentFactorsConfig;
    conditions: Condition[];
};

/**
 * Represents both saved and unsaved condition configurations built from
 * treatment and factor combinations. When an experiment is in draft,
 * these are used to combine and configure conditions, and when they
 * are saved in draft mode, we delete the existing saved condition
 * configurations and insert all new ones.
 *
 * Once an experiment is finalized, these represent "real" conditions
 * and we no longer allow the treatment/factor combination functionality
 * and only support editing individual condition records (due to foreign
 * key constraints that exist once the experiment is finalized).
 */
export type UpsertCondition = {
    id: string | null;
    name: string;
    number: number;
    treatment: Treatment;
    applicationFactors: SavedApplicationFactor[];
    adHocFactors: SavedAdHocFactor[];
    globalRegistryFactors: SavedGlobalRegistryFactor[];
};

/**
 * A DTO version of UpsertCondition suitable for API calls (sending id's
 * instead of objects)
 */
export type UpsertConditionDTO = {
    id: string | null;
    name: string;
    number: number;
    treatmentId: string;
    applicationFactorIds: string[];
    adHocFactorIds: string[];
    globalRegistryFactorIds: string[];
};

/**
 * Only allowed fields for updating a Condition
 */
export type UpdateCondition = {
    id: string;
    name: string;
};

export function mapUpsertConditionToDTO(upsertCondition: UpsertCondition): UpsertConditionDTO {
    return {
        id: upsertCondition.id,
        name: upsertCondition.name,
        number: upsertCondition.number,
        treatmentId: upsertCondition.treatment.id,
        applicationFactorIds: upsertCondition.applicationFactors.map((f) => f.id),
        adHocFactorIds: upsertCondition.adHocFactors.map((f) => f.id),
        globalRegistryFactorIds: upsertCondition.globalRegistryFactors.map((f) => f.id),
    };
}

export function isEqualConditionAssociations(
    conditionA: UpsertCondition,
    conditionB: UpsertCondition
): boolean {
    if (conditionA.treatment.id !== conditionB.treatment.id) {
        return false;
    }

    const conditionAFactorIds = collectFactorIds(conditionA);
    const conditionBFactorIds = collectFactorIds(conditionB);

    return (
        equalIgnoringOrder(
            conditionAFactorIds.applicationFactorIds,
            conditionBFactorIds.applicationFactorIds
        ) &&
        equalIgnoringOrder(
            conditionAFactorIds.adHocFactorIds,
            conditionBFactorIds.adHocFactorIds
        ) &&
        equalIgnoringOrder(
            conditionAFactorIds.globalRegistryFactorIds,
            conditionBFactorIds.globalRegistryFactorIds
        )
    );
}

function collectFactorIds(condition: UpsertCondition) {
    const appFactorIds = condition.applicationFactors.map((f) => f.id);
    const adHocFactorIds = condition.adHocFactors.map((f) => f.id);
    const globalRegistryFactorIds = condition.globalRegistryFactors.map((f) => f.id);

    return {
        applicationFactorIds: appFactorIds,
        adHocFactorIds,
        globalRegistryFactorIds,
    };
}

export const conditionsAPI = {
    fetchManageConditionsDTOForExperiment: async (
        experimentCode: string
    ): Promise<ManageConditionsDTO> => {
        return get(`/conditions/${experimentCode}`);
    },

    upsertConditionsDTOForExperiment: async (
        experimentCode: string,
        upsertConditionDTOs: UpsertConditionDTO[]
    ): Promise<Condition[]> => {
        return post(`/conditions/${experimentCode}`, upsertConditionDTOs);
    },

    updateCondition(experimentCode: string, condition: UpdateCondition): Promise<Condition[]> {
        return post(`/conditions/${experimentCode}/${condition.id}`, condition);
    },
};
